//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import React from 'react';
import styled, { css } from 'styled-components';

import Popover, {
  Props as PopoverProps,
} from '@components/molecules/popover/Popover';

type TooltipSize = 'small' | 'medium';

type TooltipProps = Omit<PopoverProps, 'shouldShowArrow' | 'shouldOpenOnHover'>;
interface IOwnProps extends TooltipProps {
  size: TooltipSize;
  shouldRemovePadding?: boolean;
}
type Props = IOwnProps;

/**
 * This component exists for the purpose of standardizing different
 * sizes of tooltips, do not use it directly.
 */
const Tooltip = ({
  /**
   * Styling for Tooltips should be applied individually to their targets or contents,
   * and not passed in through the className prop as this applies styling to an internal
   * div of the Popover. This prop should never be used, so we explicitly filter it out.
   */
  className,

  content,
  placement = 'bottom',
  shouldRemovePadding,
  size,
  ...popoverProps
}: Props) => {
  return (
    <Popover
      content={
        <StyledContent $shouldRemovePadding={shouldRemovePadding} $size={size}>
          {content}
        </StyledContent>
      }
      placement={placement}
      shouldOpenOnHover={true}
      shouldShowArrow={true}
      {...popoverProps}
    />
  );
};

const StyledContent = styled.div<{
  $size: TooltipSize;
  $shouldRemovePadding?: boolean;
}>(
  ({ $shouldRemovePadding, $size, theme }) => css`
    width: max-content;
    max-width: ${$size === 'small' ? theme.smallMeasure : theme.mediumMeasure};
    ${$shouldRemovePadding ? '' : `padding: ${theme.spacings[2]};`}
  `,
);

/**
 * The SmallTooltip molecule is a wrapper around the Popover molecule
 * that provides standardized styling and behavior configurations.
 *
 * The SmallTooltip is the standard styling for the majority of
 * tooltips across the dashboard, and should be favored over other sizes.
 */
export const SmallTooltip = (props: Omit<Props, 'size'>) => (
  <Tooltip size='small' {...props} />
);

/**
 * The SmallTooltipForIcon molecule is the same as SmallTooltip, but adds an IconWrapper.
 * The Icon component has `pointer-events: none;` by default for accessibility purposes,
 * but means that hovering the Icon on Safari does not trigger the tooltip. This
 * molecule adds a span wrapper enabling the Icon to be a proper target.
 */
export const SmallTooltipForIcon = (props: Omit<Props, 'size'>) => (
  <Tooltip
    {...props}
    size='small'
    target={<IconWrapper>{props.target}</IconWrapper>}
  />
);

/**
 * The MediumTooltip molecule is a wrapper around the Popover molecule
 * that provides standardized styling and behavior configurations.
 *
 * The MediumTooltip should only be used at design's direction,
 * as the SmallTooltip is the standard sizing for the majority
 * of contexts on the dashboard.
 */
export const MediumTooltip = (props: Omit<Props, 'size'>) => (
  <Tooltip size='medium' {...props} />
);

/**
 * The MediumTooltipForIcon molecule is the same as MediumTooltip, but adds an IconWrapper.
 * The Icon component has `pointer-events: none;` by default for accessibility purposes,
 * but means that hovering the Icon on Safari does not trigger the tooltip. This
 * molecule adds a span wrapper enabling the Icon to be a proper target.
 */
export const MediumTooltipForIcon = (props: Omit<Props, 'size'>) => (
  <Tooltip
    {...props}
    size='medium'
    target={<IconWrapper>{props.target}</IconWrapper>}
  />
);

const IconWrapper = styled.span`
  display: inline-block;
`;
